import {
  type LanguageModelV2Prompt,
  UnsupportedFunctionalityError,
} from '@ai-sdk/provider';
import type {
  GoogleGenerativeAIContent,
  GoogleGenerativeAIContentPart,
  GoogleGenerativeAIFunctionResponsePart,
  GoogleGenerativeAIPrompt,
} from './google-generative-ai-prompt';
import { convertToBase64 } from '@ai-sdk/provider-utils';

export function convertToGoogleGenerativeAIMessages(
  prompt: LanguageModelV2Prompt,
  options?: { isGemmaModel?: boolean; supportsFunctionResponseParts?: boolean },
): GoogleGenerativeAIPrompt {
  const systemInstructionParts: Array<{ text: string }> = [];
  const contents: Array<GoogleGenerativeAIContent> = [];
  let systemMessagesAllowed = true;
  const isGemmaModel = options?.isGemmaModel ?? false;
  const supportsFunctionResponseParts =
    options?.supportsFunctionResponseParts ?? true;

  for (const { role, content } of prompt) {
    switch (role) {
      case 'system': {
        if (!systemMessagesAllowed) {
          throw new UnsupportedFunctionalityError({
            functionality:
              'system messages are only supported at the beginning of the conversation',
          });
        }

        systemInstructionParts.push({ text: content });
        break;
      }

      case 'user': {
        systemMessagesAllowed = false;

        const parts: GoogleGenerativeAIContentPart[] = [];

        for (const part of content) {
          switch (part.type) {
            case 'text': {
              parts.push({ text: part.text });
              break;
            }

            case 'file': {
              // default to image/jpeg for unknown image/* types
              const mediaType =
                part.mediaType === 'image/*' ? 'image/jpeg' : part.mediaType;

              parts.push(
                part.data instanceof URL
                  ? {
                      fileData: {
                        mimeType: mediaType,
                        fileUri: part.data.toString(),
                      },
                    }
                  : {
                      inlineData: {
                        mimeType: mediaType,
                        data: convertToBase64(part.data),
                      },
                    },
              );

              break;
            }
          }
        }

        contents.push({ role: 'user', parts });
        break;
      }

      case 'assistant': {
        systemMessagesAllowed = false;

        contents.push({
          role: 'model',
          parts: content
            .map(part => {
              const thoughtSignature =
                part.providerOptions?.google?.thoughtSignature != null
                  ? String(part.providerOptions.google?.thoughtSignature)
                  : undefined;

              switch (part.type) {
                case 'text': {
                  return part.text.length === 0
                    ? undefined
                    : {
                        text: part.text,
                        thoughtSignature,
                      };
                }

                case 'reasoning': {
                  return part.text.length === 0
                    ? undefined
                    : {
                        text: part.text,
                        thought: true,
                        thoughtSignature,
                      };
                }

                case 'file': {
                  if (part.mediaType !== 'image/png') {
                    throw new UnsupportedFunctionalityError({
                      functionality:
                        'Only PNG images are supported in assistant messages',
                    });
                  }

                  if (part.data instanceof URL) {
                    throw new UnsupportedFunctionalityError({
                      functionality:
                        'File data URLs in assistant messages are not supported',
                    });
                  }

                  return {
                    inlineData: {
                      mimeType: part.mediaType,
                      data: convertToBase64(part.data),
                    },
                  };
                }

                case 'tool-call': {
                  return {
                    functionCall: {
                      ...(part.toolCallId != null
                        ? { id: part.toolCallId }
                        : {}),
                      name: part.toolName,
                      args: part.input,
                    },
                    thoughtSignature,
                  };
                }
              }
            })
            .filter(part => part !== undefined),
        });
        break;
      }

      case 'tool': {
        systemMessagesAllowed = false;

        const parts: GoogleGenerativeAIContentPart[] = [];

        for (const part of content) {
          const output = part.output;

          if (output.type === 'content') {
            if (supportsFunctionResponseParts) {
              appendToolResultParts({ parts, part, output });
            } else {
              appendLegacyToolResultParts({ parts, part, output });
            }
          } else {
            parts.push({
              functionResponse: {
                ...(part.toolCallId != null ? { id: part.toolCallId } : {}),
                name: part.toolName,
                response: {
                  name: part.toolName,
                  content: output.value,
                },
              },
            });
          }
        }

        contents.push({
          role: 'user',
          parts,
        });
        break;
      }
    }
  }

  if (
    isGemmaModel &&
    systemInstructionParts.length > 0 &&
    contents.length > 0 &&
    contents[0].role === 'user'
  ) {
    const systemText = systemInstructionParts
      .map(part => part.text)
      .join('\n\n');

    contents[0].parts.unshift({ text: systemText + '\n\n' });
  }

  return {
    systemInstruction:
      systemInstructionParts.length > 0 && !isGemmaModel
        ? { parts: systemInstructionParts }
        : undefined,
    contents,
  };
}

function appendToolResultParts({
  parts,
  part,
  output,
}: {
  parts: GoogleGenerativeAIContentPart[];
  part: { toolName: string; toolCallId?: string };
  output: {
    type: 'content';
    value: Array<
      | { type: 'text'; text: string }
      | { type: 'media'; data: string; mediaType: string }
    >;
  };
}) {
  const responseTextParts: string[] = [];
  const functionResponseParts: GoogleGenerativeAIFunctionResponsePart[] = [];

  for (const contentPart of output.value) {
    switch (contentPart.type) {
      case 'text':
        responseTextParts.push(contentPart.text);
        break;
      case 'media':
        functionResponseParts.push({
          inlineData: {
            mimeType: contentPart.mediaType,
            data: contentPart.data,
          },
        });
        break;
    }
  }

  const responseText =
    responseTextParts.length > 0
      ? responseTextParts.join('\n')
      : 'Tool executed successfully.';

  parts.push({
    functionResponse: {
      ...(part.toolCallId != null ? { id: part.toolCallId } : {}),
      name: part.toolName,
      response: {
        name: part.toolName,
        content: responseText,
      },
      ...(functionResponseParts.length > 0
        ? { parts: functionResponseParts }
        : {}),
    },
  });
}

function appendLegacyToolResultParts({
  parts,
  part,
  output,
}: {
  parts: GoogleGenerativeAIContentPart[];
  part: { toolName: string; toolCallId?: string };
  output: {
    type: 'content';
    value: Array<
      | { type: 'text'; text: string }
      | { type: 'media'; data: string; mediaType: string }
    >;
  };
}) {
  for (const contentPart of output.value) {
    switch (contentPart.type) {
      case 'text':
        parts.push({
          functionResponse: {
            ...(part.toolCallId != null ? { id: part.toolCallId } : {}),
            name: part.toolName,
            response: {
              name: part.toolName,
              content: contentPart.text,
            },
          },
        });
        break;
      case 'media':
        parts.push(
          {
            inlineData: {
              mimeType: contentPart.mediaType,
              data: contentPart.data,
            },
          },
          {
            text: 'Tool executed successfully and returned this image as a response',
          },
        );
        break;
      default:
        parts.push({ text: JSON.stringify(contentPart) });
        break;
    }
  }
}
